import {
	getRoles,
	getPermissionGroups,
	updateUser,
	getUser,
	activateAccount,
	deactivateAccount,
	deleteAccount,
	getUsernames,
} from '../../../api';
import { onRequest } from '../..';
import { PropertyType, ActionsType, CUSTOM_ROLE, DEFAULT_PERMISSIONS } from '../../../common';

const userStore = (user: PropertyType) => {
	const { id, username, isActive, permissions, roles } = user;
	const customRole = !!roles.find((r: PropertyType) => r.value === CUSTOM_ROLE.value);

	return {
		userId: id,
		username,
		isActive,
		activePermissions: Object.keys(permissions),
		activeRoles: roles,
		customRole,
	};
};

const userInit = async (payload: PropertyType) => {
	const { token, id } = payload;
	const user = await getUser({ token, uid: id });

	return userStore(user);
};

const rolesInit = async (data: PropertyType) => {
	const dataRoles = await getRoles(data);
	const rolePermissions: PropertyType = {};
	const roles: PropertyType[] = [];

	dataRoles.forEach((r: PropertyType) => {
		rolePermissions[r.name] = r.permissions;
		roles.push({ label: r.name, value: r.name });
	});

	return { rolePermissions, roles };
};

const usersInit = async (payload: PropertyType) => {
	const data = await getUsernames(payload);

	return { users: data };
};

const permissionsInit = async (token: string) => {
	const data = await getPermissionGroups({ token });

	return { permissionGroups: data };
};

const settingsSubmit = async (state: PropertyType, token: string) => {
	const success = await updateUser({ token, ...state });

	return { globalErrors: [], globalSuccess: success };
};

const userSubmit = async (state: PropertyType, payload: PropertyType) => {
	const success = await updateUser({ token: payload, ...state });

	return { globalErrors: [], globalSuccess: success };
};

const getSettings = (activeSettings: string[], setting: string) => {
	const settingIndex = activeSettings.indexOf(setting);

	settingIndex > -1 ? activeSettings.splice(settingIndex, 1) : activeSettings.push(setting);

	return activeSettings;
};

const onPermissionsCopy = async (state: PropertyType, payload: string) => {
	const { activeUsers } = state;
	const { permissions, roles } = await getUser({
		token: payload,
		uid: activeUsers[0].value,
	});

	return {
		activePermissions: Object.keys(permissions),
		activeRoles: roles,
		customRole: roles[0].value === 'Custom',
	};
};

const onPermissionsUpdate = (state: PropertyType, payload: string) => {
	const { activePermissions } = state;
	const permissions = getSettings(activePermissions, payload);

	return {
		activePermissions: permissions,
		activeRoles: [],
		customRole: true,
	};
};

const onAllPermissionsUpdate = (state: PropertyType, payload: string) => {
	const activePermissions = payload;

	return { activePermissions, activeRoles: [], customRole: true };
};

const onRolesUpdate = (state: PropertyType, payload: PropertyType[]) => {
	const { rolePermissions } = state;
	const activePermissions: string[] = [...DEFAULT_PERMISSIONS];
	const activeRoles = payload.filter((r) => r.value !== CUSTOM_ROLE.value);
	const customRole = !activeRoles.length;

	activeRoles.forEach((r: PropertyType) => {
		rolePermissions[r.value].forEach((p: string) => !activePermissions.includes(p) && activePermissions.push(p));
	});

	return { activeRoles, activePermissions, customRole };
};

const getUpdatedUser = async (state: PropertyType, payload: PropertyType) => {
	const user = await getUser({ uid: state.userId, token: payload.token });
	payload.setUser(user);
};

const onAccountDelete = async (state: PropertyType, token: string) => {
	const success = await deleteAccount({
		token,
		userIds: [state.userId],
	});

	return { globalErrors: [], globalSuccess: success };
};

const onAccountActivate = async (state: PropertyType, token: string) => {
	const success = await activateAccount({
		token,
		userIds: [state.userId],
	});

	return { globalErrors: [], globalSuccess: success };
};

const onAccountDeactivate = async (state: PropertyType, token: string) => {
	const success = await deactivateAccount({
		token,
		userIds: [state.userId],
	});

	return { globalErrors: [], globalSuccess: success };
};

const onGroupOpen = (state: PropertyType, payload: string) => {
	const { openedGroups } = state;

	openedGroups.push(payload);

	return { openedGroups };
};

const onGroupClose = (state: PropertyType, payload: string) => {
	const { openedGroups } = state;
	const groupIndex = openedGroups.indexOf(payload);

	openedGroups.splice(groupIndex, 1);

	return { openedGroups };
};

export const actions: ActionsType = {
	USER_INIT: (state, payload) => onRequest(() => userInit(payload), true),
	ROLES_INIT: (state, payload) => onRequest(() => rolesInit(payload), true),
	ROLES_UPDATE: (state, payload) => onRolesUpdate(state, payload),
	USERS_INIT: (state, payload) => onRequest(() => usersInit(payload), true),
	USERS_UPDATE: (state, payload) => ({ activeUsers: payload }),
	PERMISSIONS_INIT: (state, payload) => onRequest(() => permissionsInit(payload), true),
	PERMISSIONS_COPY: (state, payload) => onRequest(() => onPermissionsCopy(state, payload), true),
	PERMISSIONS_UPDATE: (state, payload) => onPermissionsUpdate(state, payload),
	ALL_PERMISSIONS_UPDATE: (state, payload) => onAllPermissionsUpdate(state, payload),
	GROUP_OPEN: (state, payload) => onGroupOpen(state, payload),
	GROUP_CLOSE: (state, payload) => onGroupClose(state, payload),
	GLOBAL_ERROR: (state, payload) => ({ globalErrors: payload }),
	GLOBAL_SUCCESS: (state, payload) => ({ globalSuccess: payload }),
	GET_UPDATED_USER: (state, payload) => onRequest(() => getUpdatedUser(state, payload)),
	SETTINGS_SUBMIT: (state, payload) => onRequest(() => settingsSubmit(state, payload), true),
	DELETE_ACCOUNT: (state, payload) => onRequest(() => onAccountDelete(state, payload), true),
	ACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountActivate(state, payload), true),
	DEACTIVATE_ACCOUNT: (state, payload) => onRequest(() => onAccountDeactivate(state, payload), true),
	USER_SUBMIT: (state, payload) => onRequest(() => userSubmit(state, payload), true),
	MESSAGE_INVALIDATE: () => ({ globalErrors: [], globalSuccess: '' }),
};
